import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import './Player.css';

const THUMB_COLORS = ['#5C6B5A','#7A6B55','#5A6B7A','#6B5A7A','#5A7A6B','#7A5A5A'];

function fmt(secs) {
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function Player({ session, onClose }) {
  const { toggleFavorite, favorites, saveProgress, recordPlay, progress: allProgress } = useApp();

  const audioRef   = useRef(null);
  const saveTimer  = useRef(null);
  const playLogged = useRef(false);

  const savedState     = allProgress[session.id];
  const initialTrack   = savedState?.trackIndex || 0;
  const initialPos     = savedState?.position   || 0;

  const [trackIdx, setTrackIdx]   = useState(initialTrack);
  const [playing, setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(initialPos);
  const [duration, setDuration]   = useState(0);
  const [loaded, setLoaded]       = useState(false);

  const tracks   = session.audioFiles || [];
  const isFav    = !!favorites[session.id];
  const bgColor  = THUMB_COLORS[(session.id - 1) % THUMB_COLORS.length];
  const totalPct = () => {
    if (!duration) return 0;
    const trackDurSum = tracks.reduce((a, t) => a + (t.durationSeconds || 0), 0) || duration * tracks.length;
    const elapsed = tracks.slice(0, trackIdx).reduce((a, t) => a + (t.durationSeconds || 0), 0) + currentTime;
    return Math.min(100, (elapsed / trackDurSum) * 100);
  };

  // ── load audio ─────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !tracks[trackIdx]) return;
    setLoaded(false);
    setPlaying(false);

    audio.src = tracks[trackIdx].streamUrl;
    audio.load();

    const onLoaded = () => {
      setDuration(audio.duration);
      setLoaded(true);
      if (trackIdx === initialTrack && initialPos > 0) {
        audio.currentTime = initialPos;
        setCurrentTime(initialPos);
      }
    };
    const onTime = () => setCurrentTime(audio.currentTime);
    const onEnded = () => {
      if (trackIdx < tracks.length - 1) {
        setTrackIdx(t => t + 1);
      } else {
        setPlaying(false);
      }
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, [trackIdx]);

  // ── sync play/pause ────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !loaded) return;
    if (playing) {
      audio.play().catch(() => setPlaying(false));
      // record play once per session open
      if (!playLogged.current) {
        recordPlay(session.id);
        playLogged.current = true;
      }
    } else {
      audio.pause();
    }
  }, [playing, loaded]);

  // ── save progress every 30s and on unmount ─────────────
  const doSave = useCallback(() => {
    const pct = totalPct();
    saveProgress(session.id, pct, trackIdx, audioRef.current?.currentTime || 0);
  }, [trackIdx, currentTime, duration]);

  useEffect(() => {
    clearInterval(saveTimer.current);
    saveTimer.current = setInterval(doSave, 30000);
    return () => {
      clearInterval(saveTimer.current);
      doSave(); // save on unmount
    };
  }, [doSave]);

  // ── controls ───────────────────────────────────────────
  function togglePlay() { setPlaying(p => !p); }

  function seek(e) {
    const bar = e.currentTarget;
    const r   = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  }

  function prevTrack() {
    if (currentTime > 3) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    } else if (trackIdx > 0) {
      setTrackIdx(t => t - 1);
    }
  }

  function nextTrack() {
    if (trackIdx < tracks.length - 1) setTrackIdx(t => t + 1);
  }

  const progPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="player-screen">
      <audio ref={audioRef} preload="metadata" />

      {/* background */}
      <div
        className="player-bg"
        style={{
          backgroundImage: session.imageUrl ? `url(${session.imageUrl})` : 'none',
          backgroundColor: bgColor,
        }}
      />
      <div className="player-grain" />

      <div className="player-content">
        {/* top bar */}
        <div className="player-topbar">
          <button className="player-back-btn" onClick={() => { doSave(); onClose(); }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Volver</span>
          </button>
          <button className="player-fav-btn" onClick={() => toggleFavorite(session.id)}>
            <span className={isFav ? 'fav-active' : 'fav-inactive'}>♥</span>
          </button>
        </div>

        {/* artwork */}
        <div className="player-art-wrap">
          <div className="player-art">
            {session.imageUrl
              ? <img src={session.imageUrl} alt={session.title} className="player-art-img" />
              : (
                <svg width="80" height="80" viewBox="0 0 68 68" fill="none" opacity=".25">
                  <circle cx="34" cy="34" r="22" stroke="#F5F0E8" strokeWidth="0.5" />
                  <circle cx="34" cy="34" r="12" stroke="#F5F0E8" strokeWidth="0.5" />
                  <circle cx="34" cy="34" r="4" fill="#F5F0E8" />
                </svg>
              )
            }
          </div>
        </div>

        {/* track pills */}
        {tracks.length > 1 && (
          <div className="player-pills">
            {tracks.map((t, i) => (
              <button
                key={i}
                className={`player-pill ${i === trackIdx ? 'active' : ''}`}
                onClick={() => { setTrackIdx(i); setPlaying(false); }}
              >
                Parte {i + 1}
                {t.durationSeconds ? ` · ${Math.floor(t.durationSeconds / 60)} min` : ''}
              </button>
            ))}
          </div>
        )}

        {/* info */}
        <div className="player-info">
          <div className="player-code">{session.code}</div>
          <div className="player-title">{session.title}</div>
          <div className="player-date">
            {new Date(session.classDate).toLocaleDateString('es-AR', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </div>
        </div>

        {/* progress bar */}
        <div className="player-progress-area">
          <div className="player-prog-bar" onClick={seek}>
            <div className="player-prog-inner">
              <div className="player-prog-fill" style={{ width: `${progPct}%` }} />
              <div className="player-prog-thumb" style={{ left: `${progPct}%` }} />
            </div>
          </div>
          <div className="player-times">
            <span>{fmt(currentTime)}</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>

        {/* controls */}
        <div className="player-controls">
          <button className="ctrl-side" onClick={prevTrack} disabled={trackIdx === 0 && currentTime < 1}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 5L10 12L18 19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="6" y1="5" x2="6" y2="19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </button>

          <button className="ctrl-main" onClick={togglePlay} disabled={!loaded}>
            {playing ? (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="3" y="2" width="5" height="18" rx="2" fill="currentColor" />
                <rect x="14" y="2" width="5" height="18" rx="2" fill="currentColor" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <polygon points="5,2 20,11 5,20" fill="currentColor" />
              </svg>
            )}
          </button>

          <button className="ctrl-side" onClick={nextTrack} disabled={trackIdx >= tracks.length - 1}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 5L14 12L6 19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="18" y1="5" x2="18" y2="19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
